<template>
  <div class="mt-2">
    <b-breadcrumb class="breadcrumb-pipes">
      <b-breadcrumb-item href="/settings/admin-panel/">Admin Panel</b-breadcrumb-item>
      <b-breadcrumb-item active>Manage Account Groups</b-breadcrumb-item>
    </b-breadcrumb>

    <b-card title="Manage Account Groups">
      <b-overlay :show="loading">
        <b-row align-h="between" class="mb-1">
          <b-col cols="12" md="3">
            <b-form-input v-model="filter" placeholder="Search..." />
          </b-col>
          <b-col cols="12" md="3" class="d-flex justify-content-end">
            <b-button variant="success" @click="openCreateModal">
              Create New Group
            </b-button>
          </b-col>
        </b-row>

        <b-table
          striped
          hover
          :items="groups"
          :filter="filter"
          :fields="tableColumns"
          :current-page="currentPage"
          :per-page="perPage"
          @filtered="onFiltered"
        >
          <template #cell(index)="data">
            <strong class="text-primary">#{{ data.index + 1 }}</strong>
          </template>
          <template #cell(actions)="data">
            <b-button
              size="sm"
              variant="flat-primary"
              class="btn-icon"
              @click="openEditModal(data.item.AccountGroup)"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
            <b-button
              size="sm"
              variant="flat-danger"
              class="btn-icon"
              @click="confirmDelete(data.item.AccountGroup)"
            >
              <feather-icon icon="TrashIcon" />
            </b-button>
          </template>
        </b-table>

        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
        />
      </b-overlay>
    </b-card>

    <!-- Create / Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? 'Edit Account Group' : 'Create Account Group'"
      size="lg"
      @ok="handleSave"
      ok-title="Save"
      :ok-disabled="modalLoading"
      no-close-on-backdrop
    >
      <b-overlay :show="modalLoading">
        <b-form>
          <b-form-group label="Group Name">
            <b-form-input
              v-model="form.AccountGroup"
              placeholder="Enter group name"
              :disabled="isEdit"
            />
          </b-form-group>

          <b-form-group label="Accounts">
            <v-select
              v-model="form.accounts"
              :options="availableAccounts"
              label="AccountName"
              multiple
              :reduce="val => val"
              placeholder="Select accounts..."
            />
          </b-form-group>

          <b-form-group label="Assigned Users (Client)">
            <v-select
              v-model="form.users"
              :options="clientUsers"
              :label="userLabel"
              multiple
              :reduce="val => val"
              placeholder="Select users..."
            >
              <template #option="{ name, surname, email }">
                {{ name }} {{ surname }} <small class="text-muted">({{ email }})</small>
              </template>
              <template #selected-option="{ name, surname }">
                {{ name }} {{ surname }}
              </template>
            </v-select>
          </b-form-group>
        </b-form>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BTable, BPagination,
  BFormInput, BFormGroup, BForm, BModal, BBreadcrumb,
  BBreadcrumbItem, BOverlay,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import { onUnmounted } from '@vue/composition-api'
import settingsStoreModule from '../../settingsStoreModule'

export default {
  components: {
    BCard, BRow, BCol, BButton, BTable, BPagination,
    BFormInput, BFormGroup, BForm, BModal, BBreadcrumb,
    BBreadcrumbItem, BOverlay, vSelect,
  },

  data() {
    return {
      loading: false,
      modalLoading: false,
      groups: [],
      availableAccounts: [],
      clientUsers: [],
      filter: '',
      currentPage: 1,
      perPage: 15,
      totalRows: 0,
      showModal: false,
      isEdit: false,
      editOriginalName: null,
      form: {
        AccountGroup: '',
        accounts: [],
        users: [],
      },
      tableColumns: [
        { key: 'index', label: '' },
        { key: 'AccountGroup', label: 'Group Name', sortable: true },
        { key: 'account_count', label: 'Accounts', sortable: true },
        { key: 'user_count', label: 'Assigned Users', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },

  mounted() {
    this.loadGroups()
    this.loadSupportData()
  },

  methods: {
    loadGroups() {
      this.loading = true
      store.dispatch('settings/fetchAccountGroups')
        .then(res => {
          this.groups = res.data
          this.totalRows = res.data.length
        })
        .finally(() => { this.loading = false })
    },

    loadSupportData() {
      store.dispatch('settings/fetchAccountGroupAvailableAccounts')
        .then(res => { this.availableAccounts = res.data })
      store.dispatch('settings/fetchAccountGroupClientUsers')
        .then(res => { this.clientUsers = res.data })
    },

    openCreateModal() {
      this.isEdit = false
      this.editOriginalName = null
      this.form = { AccountGroup: '', accounts: [], users: [] }
      this.showModal = true
    },

    openEditModal(groupName) {
      this.isEdit = true
      this.editOriginalName = groupName
      this.form = { AccountGroup: groupName, accounts: [], users: [] }
      this.modalLoading = true
      this.showModal = true

      store.dispatch('settings/fetchAccountGroupDetail', groupName)
        .then(res => {
          this.form.accounts = res.data.accounts
          this.form.users = res.data.users
        })
        .finally(() => { this.modalLoading = false })
    },

    handleSave(bvModalEvt) {
      bvModalEvt.preventDefault()
      if (!this.form.AccountGroup.trim()) {
        this.$bvToast.toast('Group name is required.', { variant: 'danger', title: 'Validation' })
        return
      }
      if (!this.form.accounts.length) {
        this.$bvToast.toast('At least one account is required.', { variant: 'danger', title: 'Validation' })
        return
      }

      const payload = {
        AccountGroup: this.form.AccountGroup.trim(),
        accounts: this.form.accounts,
        user_ids: this.form.users.map(u => u.id),
      }

      this.modalLoading = true
      const action = this.isEdit
        ? store.dispatch('settings/updateAccountGroup', { name: this.editOriginalName, payload })
        : store.dispatch('settings/createAccountGroup', payload)

      action
        .then(() => {
          this.$bvToast.toast('Account group saved.', { variant: 'success', title: 'Success' })
          this.showModal = false
          this.loadGroups()
        })
        .catch(() => {
          this.$bvToast.toast('An error occurred.', { variant: 'danger', title: 'Error' })
        })
        .finally(() => { this.modalLoading = false })
    },

    confirmDelete(groupName) {
      this.$bvModal.msgBoxConfirm(`Delete account group "${groupName}"? This cannot be undone.`, {
        title: 'Confirm Delete',
        okVariant: 'danger',
        okTitle: 'Delete',
      }).then(confirmed => {
        if (!confirmed) return
        store.dispatch('settings/deleteAccountGroup', groupName)
          .then(() => {
            this.$bvToast.toast('Account group deleted.', { variant: 'success', title: 'Deleted' })
            this.loadGroups()
          })
      })
    },

    onFiltered(items) {
      this.totalRows = items.length
      this.currentPage = 1
    },

    userLabel(user) {
      return `${user.name} ${user.surname}`
    },
  },

  setup() {
    const STORE_KEY = 'settings'
    if (!store.hasModule(STORE_KEY)) store.registerModule(STORE_KEY, settingsStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_KEY)) store.unregisterModule(STORE_KEY)
    })
  },
}
</script>
